/* eslint-disable react-hooks/set-state-in-effect */
import { isNil } from '@sixfold/typed-primitives';
import classnames from 'classnames';
import { scaleLinear, ScaleTime } from 'd3-scale';
import { utcFormat } from 'd3-time-format';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Compute tick values at nice UTC intervals, adapting to zoom level
const computeUtcTicks = (timeScale: ScaleTime<number, number>): Date[] => {
  const domain = timeScale.domain();
  const start = domain[0];
  const end = domain[1];
  if (!start || !end) return [];

  const durationMs = end.getTime() - start.getTime();
  const durationHours = durationMs / (1000 * 60 * 60);

  // Choose interval based on visible duration to get ~8-12 ticks
  // Intervals must divide 24 evenly for nice alignment: 1, 2, 3, 4, 6, 8, 12, 24
  let intervalHours: number;
  if (durationHours <= 6) {
    intervalHours = 1;
  } else if (durationHours <= 12) {
    intervalHours = 2;
  } else if (durationHours <= 24) {
    intervalHours = 3;
  } else if (durationHours <= 48) {
    intervalHours = 6;
  } else if (durationHours <= 96) {
    intervalHours = 12;
  } else {
    intervalHours = 24;
  }

  // Find first tick at interval boundary
  const startHour = start.getUTCHours();
  const firstTickHour = Math.ceil(startHour / intervalHours) * intervalHours;
  const firstTick = new Date(
    Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate(), firstTickHour, 0, 0, 0),
  );
  if (firstTick < start) {
    firstTick.setUTCHours(firstTick.getUTCHours() + intervalHours);
  }

  const ticks: Date[] = [];
  const cursor = new Date(firstTick);
  while (cursor <= end) {
    ticks.push(new Date(cursor));
    cursor.setUTCHours(cursor.getUTCHours() + intervalHours);
  }
  return ticks;
};

import { useTimelineColors } from './timeline';
import type { EtaDataPoint, Stop, StopEtaTimeSeries } from './types';

import styles from './eta_chart.module.css';

// Helper to create SVG path from data points
const createLinePath = (points: Array<{ x: number; y: number | null }>): string => {
  const validPoints = points.filter((p) => p.y !== null && isFinite(p.y as number));
  if (validPoints.length < 2) return '';

  return validPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
};

// Segment info for hidden/visible ETA line segments
interface EtaLineSegment {
  path: string;
  isHidden: boolean;
}

// Helper to create segmented SVG paths based on hidden status
// Each segment is colored based on its starting point's hidden status
// Segments connect at transition points to ensure the line is continuous
const createSegmentedLinePaths = (
  points: Array<{ x: number; y: number | null; isHidden: boolean }>,
): EtaLineSegment[] => {
  const validPoints = points.filter((p) => p.y !== null && isFinite(p.y as number));
  if (validPoints.length < 2) return [];

  const segments: EtaLineSegment[] = [];
  let segmentStartIdx = 0;

  for (let i = 1; i < validPoints.length; i++) {
    const prevPoint = validPoints[i - 1];
    const currentPoint = validPoints[i];

    // Check if hidden status changes at this point
    if (currentPoint.isHidden !== prevPoint.isHidden) {
      // Create segment from segmentStartIdx to current point (inclusive for connection)
      const segmentPoints = validPoints.slice(segmentStartIdx, i + 1);
      if (segmentPoints.length >= 2) {
        segments.push({
          path: segmentPoints.map((p, j) => `${j === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' '),
          isHidden: validPoints[segmentStartIdx].isHidden,
        });
      }
      // Next segment starts at current point
      segmentStartIdx = i;
    }
  }

  // Add the final segment
  const finalSegmentPoints = validPoints.slice(segmentStartIdx);
  if (finalSegmentPoints.length >= 2) {
    segments.push({
      path: finalSegmentPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' '),
      isHidden: validPoints[segmentStartIdx].isHidden,
    });
  } else if (finalSegmentPoints.length === 1 && segments.length > 0) {
    // If there's only one point left but we have previous segments,
    // we need to ensure the last point is included in the previous segment
    // This case is already handled by including i+1 in the slice above
  }

  return segments;
};

// ETA line colors
export const ETA_COLORS = {
  arrivalTime: '#2196F3', // Blue for estimated_arrival (arrival_time in ETA)
  arrivalTimeHidden: '#9E9E9E', // Gray for hidden ETA
  regulatedEta: '#4CAF50', // Green for regulated ETA
  unregulatedEta: '#FF9800', // Orange for unregulated ETA
  actualArrival: '#E91E63', // Pink for actual arrival time (ATA)
  delayThreshold: '#9E9E9E', // Gray for delay threshold line
};

// Line dash patterns for different stops
const LINE_DASH_PATTERNS = [
  '', // Solid for first stop
  '8,4', // Dashed
  '4,4', // Short dashed
  '12,4,4,4', // Dash-dot
  '2,4', // Dotted
  '16,4,8,4', // Long dash-short dash
  '4,8', // Wide dotted
  '12,4,2,4,2,4', // Dash-dot-dot
];

// UTC formatters
const formatUtcDateTime = utcFormat('%Y-%m-%d %H:%M:%S');
const formatUtcDateTimeShort = utcFormat('%m-%d %H:%M');
const formatUtcTime = utcFormat('%H:%M');
const formatUtcDateShort = utcFormat('%b %d');

// Smart tick formatter: shows dates at midnight when spanning multiple days, times otherwise
const smartTickFormat = (timeScale: ScaleTime<number, number>) => {
  const domain = timeScale.domain();
  const durationHours = (domain[1].getTime() - domain[0].getTime()) / (1000 * 60 * 60);
  const spansMultipleDays = durationHours > 24;

  return (tick: Date): string => {
    if (spansMultipleDays && tick.getUTCHours() === 0 && tick.getUTCMinutes() === 0) {
      return formatUtcDateShort(tick); // e.g. "Feb 08"
    }
    return formatUtcTime(tick); // e.g. "14:00"
  };
};

// Convert a timedelta (hours) relative to a base timestamp into an absolute UTC timestamp string
const hoursToUtcTimestamp = (baseTimestamp: Date, hours: number): string => {
  const ms = baseTimestamp.getTime() + hours * 3600000;
  return formatUtcDateTimeShort(new Date(ms));
};

interface EtaChartProps {
  stops: Stop[]; // Used for reference data (passed for potential future use)
  etaData: StopEtaTimeSeries[];
  timeScale: ScaleTime<number, number>;
  width: number;
  margin: { left: number; right: number };
}

// Hover state for chart-wide tooltip
interface HoverState {
  visible: boolean;
  xPixel: number; // x position in pixels (relative to chart area)
  clientX: number;
  clientY: number;
  hoverTime: Date;
}

export const EtaChart: React.FC<EtaChartProps> = ({ stops, etaData, timeScale, width, margin }) => {
  const { colors } = useTimelineColors();
  const [selectedStopIds, setSelectedStopIds] = useState<Set<number>>(new Set());

  // Update selected stops when etaData changes - default to first and last stop only
  useEffect(() => {
    if (etaData.length > 0) {
      const sorted = [...etaData].sort((a, b) => a.sequenceNumber - b.sequenceNumber);
      const firstAndLast = new Set<number>();
      firstAndLast.add(sorted[0].stopId);
      firstAndLast.add(sorted[sorted.length - 1].stopId);
      setSelectedStopIds(firstAndLast);
    }
  }, [etaData]);

  // Hover state for chart-wide tooltip
  const [hover, setHover] = useState<HoverState | null>(null);

  // Line type visibility (click to toggle) and highlight (hover to bring to front)
  type LineType = 'eta' | 'regulated' | 'unregulated' | 'ata';
  const ALL_LINE_TYPES: LineType[] = ['eta', 'regulated', 'unregulated', 'ata'];
  const [visibleLineTypes, setVisibleLineTypes] = useState<Set<LineType>>(new Set(ALL_LINE_TYPES));
  const [highlightedLineType, setHighlightedLineType] = useState<LineType | null>(null);

  const toggleLineType = useCallback((lineType: LineType) => {
    setVisibleLineTypes((prev) => {
      const next = new Set(prev);
      if (next.has(lineType)) {
        // Don't allow hiding all lines
        if (next.size > 1) {
          next.delete(lineType);
        }
      } else {
        next.add(lineType);
      }
      return next;
    });
  }, []);

  // Helper: get opacity for a line type based on highlight state
  // When a hidden line's legend is hovered, "peek" it at full opacity without toggling visibility
  const getLineOpacity = useCallback(
    (lineType: LineType, baseOpacity: number = 0.9): number => {
      const isVisible = visibleLineTypes.has(lineType);
      const isHighlighted = highlightedLineType === lineType;
      if (isHighlighted) return 1; // peek: show even if filtered out
      if (!isVisible) return 0;
      if (highlightedLineType === null) return baseOpacity;
      return 0.15; // dim non-highlighted visible lines
    },
    [visibleLineTypes, highlightedLineType],
  );

  // Helper: get stroke width for a line type based on highlight state
  const getLineStrokeWidth = useCallback(
    (lineType: LineType, base: number = 2): number => {
      if (highlightedLineType === lineType) return base + 1;
      return base;
    },
    [highlightedLineType],
  );

  // Helper: is a line type currently shown (visible or being peeked via hover)
  const isLineTypeShown = useCallback(
    (lineType: LineType): boolean => {
      return visibleLineTypes.has(lineType) || highlightedLineType === lineType;
    },
    [visibleLineTypes, highlightedLineType],
  );

  // Toggle a stop selection
  const toggleStop = useCallback((stopId: number) => {
    setSelectedStopIds((prev) => {
      const next = new Set(prev);
      if (next.has(stopId)) {
        next.delete(stopId);
      } else {
        next.add(stopId);
      }
      return next;
    });
  }, []);

  // Select/deselect all
  const selectAll = useCallback(() => {
    setSelectedStopIds(new Set(etaData.map((s) => s.stopId)));
  }, [etaData]);

  const deselectAll = useCallback(() => {
    setSelectedStopIds(new Set());
  }, []);

  // Get filtered and ordered stops for rendering
  const selectedStops = useMemo(() => {
    return etaData.filter((s) => selectedStopIds.has(s.stopId)).sort((a, b) => a.sequenceNumber - b.sequenceNumber);
  }, [etaData, selectedStopIds]);

  // Calculate the index-based line styles (based on selection order)
  const getLineStyleIndex = useCallback(
    (stopId: number) => {
      const idx = selectedStops.findIndex((s) => s.stopId === stopId);
      return idx >= 0 ? idx % LINE_DASH_PATTERNS.length : 0;
    },
    [selectedStops],
  );

  // Get the visible time domain from the timeScale
  // Use getTime() for stable primitive dependencies since D3 scale objects can be mutated
  const domainStartMs = timeScale.domain()[0]?.getTime();
  const domainEndMs = timeScale.domain()[1]?.getTime();

  // Calculate y-axis domain (hours until arrival) - based on visible data points only
  const yDomain = useMemo(() => {
    if (selectedStops.length === 0) return [0, 24];
    if (domainStartMs === undefined || domainEndMs === undefined) return [0, 24];

    const visibleStart = new Date(domainStartMs);
    const visibleEnd = new Date(domainEndMs);
    let maxHours = -Infinity;

    selectedStops.forEach((stop) => {
      stop.dataPoints.forEach((dp) => {
        // Only consider data points within the visible time range
        if (dp.timestamp < visibleStart || dp.timestamp > visibleEnd) {
          return;
        }

        if (dp.hoursUntilArrival !== null && isFinite(dp.hoursUntilArrival) && dp.hoursUntilArrival >= 0) {
          maxHours = Math.max(maxHours, dp.hoursUntilArrival);
        }
        if (dp.hoursUntilRegulated !== null && isFinite(dp.hoursUntilRegulated) && dp.hoursUntilRegulated >= 0) {
          maxHours = Math.max(maxHours, dp.hoursUntilRegulated);
        }
        if (dp.hoursUntilUnregulated !== null && isFinite(dp.hoursUntilUnregulated) && dp.hoursUntilUnregulated >= 0) {
          maxHours = Math.max(maxHours, dp.hoursUntilUnregulated);
        }
        if (dp.hoursUntilActual !== null && isFinite(dp.hoursUntilActual) && dp.hoursUntilActual >= 0) {
          maxHours = Math.max(maxHours, dp.hoursUntilActual);
        }
      });
    });

    if (maxHours === -Infinity) maxHours = 24;

    // Add some padding at the top
    const padding = Math.max(1, maxHours * 0.1);
    return [0, maxHours + padding];
  }, [selectedStops, domainStartMs, domainEndMs]);

  // Get timeslot info for selected stops (for drawing background areas)
  const selectedStopsWithTimeslots = useMemo(() => {
    return selectedStops
      .map((etaStop) => {
        const stop = stops.find((s) => s.stop_id === etaStop.stopId);
        if (!stop?.timeslot) return null;
        return {
          stopId: etaStop.stopId,
          stopName: etaStop.stopName,
          type: stop.type,
          timeslotBegin: new Date(stop.timeslot.begin),
          timeslotEnd: new Date(stop.timeslot.end),
        };
      })
      .filter((s): s is NonNullable<typeof s> => s !== null);
  }, [selectedStops, stops]);

  // Timeslot colors matching the main timeline (with light opacity)
  const getTimeslotColor = (type: 'loading' | 'unloading' | 'waypoint' | 'other'): string => {
    return colors.timeslot[type];
  };

  // Chart dimensions
  const innerWidth = width - margin.left - margin.right;
  const chartHeight = 300;
  const chartMarginTop = 20;
  const chartMarginBottom = 30;
  const chartInnerHeight = chartHeight - chartMarginTop - chartMarginBottom;

  // Y scale (inverted: higher hours at top, 0 at bottom)
  const yScale = useMemo(
    () => scaleLinear().domain(yDomain).range([chartInnerHeight, 0]).nice(),
    [yDomain, chartInnerHeight],
  );

  // Create line path for a given accessor
  const createEtaLinePath = useCallback(
    (dataPoints: EtaDataPoint[], accessor: (dp: EtaDataPoint) => number | null): string => {
      const points = dataPoints.map((dp) => {
        const value = accessor(dp);
        return {
          x: timeScale(dp.timestamp),
          y: value !== null && isFinite(value) ? yScale(value) : null,
        };
      });
      return createLinePath(points);
    },
    [timeScale, yScale],
  );

  // Create segmented line paths for ETA with hidden status coloring
  const createEtaSegmentedLinePaths = useCallback(
    (dataPoints: EtaDataPoint[], accessor: (dp: EtaDataPoint) => number | null): EtaLineSegment[] => {
      const points = dataPoints.map((dp) => {
        const value = accessor(dp);
        return {
          x: timeScale(dp.timestamp),
          y: value !== null && isFinite(value) ? yScale(value) : null,
          isHidden: dp.isHidden,
        };
      });
      return createSegmentedLinePaths(points);
    },
    [timeScale, yScale],
  );

  // Find closest data point to a given time for a stop
  const findClosestDataPoint = useCallback((stop: StopEtaTimeSeries, targetTime: Date): EtaDataPoint | null => {
    if (stop.dataPoints.length === 0) return null;

    let closest = stop.dataPoints[0];
    let minDiff = Math.abs(closest.timestamp.getTime() - targetTime.getTime());

    for (const dp of stop.dataPoints) {
      const diff = Math.abs(dp.timestamp.getTime() - targetTime.getTime());
      if (diff < minDiff) {
        minDiff = diff;
        closest = dp;
      }
    }

    // Only return if within 30 minutes of the hover time
    if (minDiff > 30 * 60 * 1000) return null;
    return closest;
  }, []);

  // Handle mouse move over chart
  const handleChartMouseMove = useCallback(
    (e: React.MouseEvent<SVGRectElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const xPixel = e.clientX - rect.left;

      // Convert pixel position to time
      const hoverTime = timeScale.invert(xPixel);

      setHover({
        visible: true,
        xPixel,
        clientX: e.clientX,
        clientY: e.clientY,
        hoverTime,
      });
    },
    [timeScale],
  );

  const handleChartMouseLeave = useCallback(() => {
    setHover(null);
  }, []);

  // Format helper for distance
  const formatDistance = (meters: number | null): string => {
    if (meters === null) return 'N/A';
    return meters >= 1000 ? `${(meters / 1000).toFixed(1)} km` : `${meters} m`;
  };

  // Build tooltip content for all selected stops at hover time
  const buildHoverTooltipContent = useCallback(() => {
    if (!hover) return null;

    const stopsWithData = selectedStops
      .map((stop) => {
        const dataPoint = findClosestDataPoint(stop, hover.hoverTime);
        return dataPoint ? { stop, dataPoint } : null;
      })
      .filter((s): s is NonNullable<typeof s> => s !== null);

    if (stopsWithData.length === 0) return null;

    return (
      <div className={styles.tooltipContent}>
        {/* Header with hover time */}
        <div className={styles.tooltipHeader}>
          <div className={styles.tooltipTimeLabel}>Time (UTC)</div>
          <div className={styles.tooltipTimeValue}>{formatUtcDateTime(hover.hoverTime)}</div>
        </div>

        {/* Stop data sections */}
        {stopsWithData.map(({ stop, dataPoint }, index) => {
          const styleIdx = getLineStyleIndex(stop.stopId);
          const dashPattern = LINE_DASH_PATTERNS[styleIdx];

          return (
            <div
              key={stop.stopId}
              className={classnames(styles.tooltipStopSection, index > 0 && styles.tooltipStopSectionWithBorder)}>
              {/* Stop header with line style indicator */}
              <div className={styles.tooltipStopHeader}>
                <svg width="20" height="10">
                  <line
                    x1="0"
                    y1="5"
                    x2="20"
                    y2="5"
                    stroke={ETA_COLORS.arrivalTime}
                    strokeWidth="2"
                    strokeDasharray={dashPattern}
                  />
                </svg>
                <span className={styles.tooltipStopName}>
                  {stop.sequenceNumber}. {stop.stopName}
                </span>
              </div>

              {/* ETA values in a compact grid */}
              <div className={styles.tooltipDataGrid}>
                {isLineTypeShown('eta') && (
                  <>
                    <span style={{ color: dataPoint.isHidden ? ETA_COLORS.arrivalTimeHidden : ETA_COLORS.arrivalTime }}>
                      ●
                    </span>
                    <span>
                      ETA: <strong>{dataPoint.hoursUntilArrival.toFixed(2)}h</strong>{' '}
                      <span className={styles.tooltipTimestamp}>
                        ({hoursToUtcTimestamp(dataPoint.timestamp, dataPoint.hoursUntilArrival)})
                      </span>
                      {dataPoint.isHidden && <span className={styles.tooltipHiddenText}>(hidden)</span>}
                    </span>
                  </>
                )}

                {isLineTypeShown('regulated') && dataPoint.hoursUntilRegulated !== null && (
                  <>
                    <span
                      style={{
                        color: dataPoint.isHidden ? ETA_COLORS.arrivalTimeHidden : ETA_COLORS.regulatedEta,
                      }}>
                      ●
                    </span>
                    <span>
                      Reg: <strong>{dataPoint.hoursUntilRegulated.toFixed(2)}h</strong>{' '}
                      <span className={styles.tooltipTimestamp}>
                        ({hoursToUtcTimestamp(dataPoint.timestamp, dataPoint.hoursUntilRegulated)})
                      </span>
                      {dataPoint.isHidden && <span className={styles.tooltipHiddenText}>(hidden)</span>}
                    </span>
                  </>
                )}

                {isLineTypeShown('unregulated') && dataPoint.hoursUntilUnregulated !== null && (
                  <>
                    <span
                      style={{
                        color: dataPoint.isHidden ? ETA_COLORS.arrivalTimeHidden : ETA_COLORS.unregulatedEta,
                      }}>
                      ●
                    </span>
                    <span>
                      Unreg: <strong>{dataPoint.hoursUntilUnregulated.toFixed(2)}h</strong>{' '}
                      <span className={styles.tooltipTimestamp}>
                        ({hoursToUtcTimestamp(dataPoint.timestamp, dataPoint.hoursUntilUnregulated)})
                      </span>
                      {dataPoint.isHidden && <span className={styles.tooltipHiddenText}>(hidden)</span>}
                    </span>
                  </>
                )}

                {isLineTypeShown('ata') && dataPoint.hoursUntilActual !== null && (
                  <>
                    <span style={{ color: ETA_COLORS.actualArrival }}>●</span>
                    <span>
                      ATA: <strong>{dataPoint.hoursUntilActual.toFixed(2)}h</strong>{' '}
                      <span className={styles.tooltipTimestamp}>
                        ({hoursToUtcTimestamp(dataPoint.timestamp, dataPoint.hoursUntilActual)})
                      </span>
                    </span>
                  </>
                )}
              </div>

              {/* Additional metadata (collapsed by default, show key info) */}
              {(dataPoint.status || dataPoint.accuracy || dataPoint.legDistanceMeters !== null) && (
                <div className={styles.tooltipMetadata}>
                  {dataPoint.status && <span className={styles.tooltipMetadataItem}>Status: {dataPoint.status}</span>}
                  {dataPoint.accuracy && <span className={styles.tooltipMetadataItem}>Acc: {dataPoint.accuracy}</span>}
                  {dataPoint.legDistanceMeters !== null && (
                    <span>Dist: {formatDistance(dataPoint.legDistanceMeters)}</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }, [hover, selectedStops, findClosestDataPoint, getLineStyleIndex, isLineTypeShown]);

  if (etaData.length === 0) {
    return (
      <div className={styles.emptyState} style={{ color: colors.text.secondary }}>
        No ETA data available. Click &quot;Load ETA data&quot; to fetch historical ETA messages.
      </div>
    );
  }

  return (
    <div className={styles.chartContainer}>
      {/* Chart */}
      <svg width={width} height={chartHeight} className={styles.chartSvg}>
        <defs>
          <clipPath id="eta-chart-clip">
            <rect x={0} y={0} width={innerWidth} height={chartInnerHeight} />
          </clipPath>
        </defs>
        <g transform={`translate(${margin.left},${chartMarginTop})`}>
          {/* Grid lines - horizontal */}
          {yScale.ticks(8).map((tick, i) => (
            <g key={i}>
              <line
                x1={0}
                x2={innerWidth}
                y1={yScale(tick)}
                y2={yScale(tick)}
                stroke={colors.background.grid}
                strokeWidth={1}
                strokeDasharray="2,2"
              />
              <text
                x={-8}
                y={yScale(tick)}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize="10px"
                fill={colors.text.secondary}>
                {tick.toFixed(1)}h
              </text>
            </g>
          ))}

          {/* X-axis (time) ticks at nice UTC intervals */}
          {computeUtcTicks(timeScale).map((tick, i) => (
            <g key={i} transform={`translate(${timeScale(tick)},${chartInnerHeight})`}>
              <line y2={6} stroke={colors.background.grid} />
              <text y={18} textAnchor="middle" fontSize="10px" fill={colors.text.secondary}>
                {smartTickFormat(timeScale)(tick)}
              </text>
            </g>
          ))}

          {/* Axes */}
          <line x1={0} x2={innerWidth} y1={chartInnerHeight} y2={chartInnerHeight} stroke={colors.background.grid} />
          <line x1={0} x2={0} y1={0} y2={chartInnerHeight} stroke={colors.background.grid} />

          {/* Y-axis label */}
          <text
            transform={`translate(-45, ${chartInnerHeight / 2}) rotate(-90)`}
            textAnchor="middle"
            fontSize="11px"
            fill={colors.text.primary}>
            Hours until ETA
          </text>

          {/* Timeslot background areas for selected stops */}
          {selectedStopsWithTimeslots.map((stop) => {
            const x1 = timeScale(stop.timeslotBegin);
            const x2 = timeScale(stop.timeslotEnd);
            // Clamp to chart bounds
            const clampedX1 = Math.max(0, x1);
            const clampedX2 = Math.min(innerWidth, x2);
            if (clampedX2 <= clampedX1) return null;

            return (
              <rect
                key={`timeslot-${stop.stopId}`}
                x={clampedX1}
                y={0}
                width={clampedX2 - clampedX1}
                height={chartInnerHeight}
                fill={getTimeslotColor(stop.type)}
                opacity={0.15}
              />
            );
          })}

          {/* Delay threshold lines - diagonal lines from deadline/timeslot end */}
          {/* If ETA is above this line, delivery will be late; below means on time */}
          {selectedStopsWithTimeslots.map((stop) => {
            // Use timeslot end as the deadline (could add deadline field support later)
            const deadlineTime = stop.timeslotEnd;
            const deadlineX = timeScale(deadlineTime);

            // Line equation: hoursUntilDeadline = (deadline - currentTime) / 1 hour
            // At x = deadlineX, y = 0
            // At x = deadlineX - 1 hour worth of pixels, y = 1 hour
            // The line goes from (deadlineX, 0) up and to the left

            // Calculate the line endpoints
            // Start point: (deadlineX, 0) - at deadline, 0 hours until deadline
            let startX = deadlineX;
            let startY = yScale(0);

            // End point: where the line intersects the top of the chart or left edge
            // At time T, hours until deadline = (deadline - T) in hours
            // We need to find where this equals yDomain[1] (max hours) or where x = 0
            const maxHours = yDomain[1];
            const msPerHour = 1000 * 60 * 60;

            // Calculate end point based on max hours
            const endTimeForMaxHours = new Date(deadlineTime.getTime() - maxHours * msPerHour);
            let endX = timeScale(endTimeForMaxHours);
            let endY = yScale(maxHours);

            // If end would be before chart start, clip to left edge
            if (endX < 0) {
              endX = 0;
              // Calculate hours at x=0
              const timeAtLeftEdge = timeScale.invert(0);
              const hoursAtLeftEdge = (deadlineTime.getTime() - timeAtLeftEdge.getTime()) / msPerHour;
              endY = yScale(Math.max(0, hoursAtLeftEdge));
            }

            // If start would be after chart end, clip to right edge
            if (startX > innerWidth) {
              startX = innerWidth;
              // Calculate hours at x=innerWidth
              const timeAtRightEdge = timeScale.invert(innerWidth);
              const hoursAtRightEdge = (deadlineTime.getTime() - timeAtRightEdge.getTime()) / msPerHour;
              startY = yScale(Math.max(0, hoursAtRightEdge));
            }

            // Only draw if at least part of the line is visible
            if (startX < 0 || endX > innerWidth) return null;

            return (
              <line
                key={`delay-threshold-${stop.stopId}`}
                x1={startX}
                y1={startY}
                x2={Math.max(0, endX)}
                y2={endY}
                stroke={ETA_COLORS.delayThreshold}
                strokeWidth={1}
                strokeDasharray="4,4"
                opacity={0.6}
              />
            );
          })}

          {/* Draw lines for each selected stop - clipped to chart area */}
          {/* Render order: non-highlighted lines first, highlighted line type last (on top) */}
          <g clipPath="url(#eta-chart-clip)">
            {(() => {
              // Define line type render configs; order determines z-order (last = on top)
              const lineConfigs: {
                type: LineType;
                render: (stop: StopEtaTimeSeries, dashPattern: string) => React.ReactNode;
              }[] = [
                {
                  type: 'ata',
                  render: (stop, dashPattern) =>
                    stop.dataPoints.some((dp) => dp.hoursUntilActual !== null && dp.hoursUntilActual >= 0) ? (
                      <path
                        d={createEtaLinePath(stop.dataPoints, (dp) =>
                          dp.hoursUntilActual !== null && dp.hoursUntilActual >= 0 ? dp.hoursUntilActual : null,
                        )}
                        fill="none"
                        stroke={ETA_COLORS.actualArrival}
                        strokeWidth={getLineStrokeWidth('ata')}
                        strokeDasharray={dashPattern}
                        opacity={getLineOpacity('ata', 0.7)}
                        className={styles.linePath}
                      />
                    ) : null,
                },
                {
                  type: 'regulated',
                  render: (stop, dashPattern) =>
                    stop.dataPoints.some((dp) => dp.hoursUntilRegulated !== null) ? (
                      <path
                        d={createEtaLinePath(stop.dataPoints, (dp) => dp.hoursUntilRegulated)}
                        fill="none"
                        stroke={ETA_COLORS.regulatedEta}
                        strokeWidth={getLineStrokeWidth('regulated')}
                        strokeDasharray={dashPattern}
                        opacity={getLineOpacity('regulated')}
                        className={styles.linePath}
                      />
                    ) : null,
                },
                {
                  type: 'unregulated',
                  render: (stop, dashPattern) =>
                    stop.dataPoints.some((dp) => dp.hoursUntilUnregulated !== null) ? (
                      <path
                        d={createEtaLinePath(stop.dataPoints, (dp) => dp.hoursUntilUnregulated)}
                        fill="none"
                        stroke={ETA_COLORS.unregulatedEta}
                        strokeWidth={getLineStrokeWidth('unregulated')}
                        strokeDasharray={dashPattern}
                        opacity={getLineOpacity('unregulated')}
                        className={styles.linePath}
                      />
                    ) : null,
                },
                {
                  type: 'eta',
                  render: (stop, dashPattern) => {
                    const etaSegments = createEtaSegmentedLinePaths(stop.dataPoints, (dp) => dp.hoursUntilArrival);
                    return etaSegments.map((segment, segIdx) => (
                      <path
                        key={`eta-${segIdx}`}
                        d={segment.path}
                        fill="none"
                        stroke={segment.isHidden ? ETA_COLORS.arrivalTimeHidden : ETA_COLORS.arrivalTime}
                        strokeWidth={getLineStrokeWidth('eta')}
                        strokeDasharray={dashPattern}
                        opacity={getLineOpacity('eta')}
                        className={styles.linePath}
                      />
                    ));
                  },
                },
              ];

              // Sort so that the highlighted type renders last (on top)
              const sortedConfigs = highlightedLineType
                ? [...lineConfigs].sort((a, b) => {
                    if (a.type === highlightedLineType) return 1;
                    if (b.type === highlightedLineType) return -1;
                    return 0;
                  })
                : lineConfigs;

              return selectedStops.map((stop) => {
                const styleIdx = getLineStyleIndex(stop.stopId);
                const dashPattern = LINE_DASH_PATTERNS[styleIdx];

                return (
                  <g key={stop.stopId}>
                    {sortedConfigs.map((config) => (
                      <React.Fragment key={`${stop.stopId}-${config.type}`}>
                        {config.render(stop, dashPattern)}
                      </React.Fragment>
                    ))}
                  </g>
                );
              });
            })()}
          </g>

          {/* Hover vertical line indicator */}
          {hover && (
            <line
              x1={hover.xPixel}
              x2={hover.xPixel}
              y1={0}
              y2={chartInnerHeight}
              stroke="#666"
              strokeWidth={1}
              strokeDasharray="4,4"
              className={styles.hoverLine}
            />
          )}

          {/* Data point indicators at hover time */}
          {hover &&
            selectedStops.map((stop) => {
              const dataPoint = findClosestDataPoint(stop, hover.hoverTime);
              if (!dataPoint) return null;

              const x = timeScale(dataPoint.timestamp);

              return (
                <g key={`hover-${stop.stopId}`}>
                  {/* ETA point */}
                  {isLineTypeShown('eta') && (
                    <circle
                      cx={x}
                      cy={yScale(dataPoint.hoursUntilArrival)}
                      r={highlightedLineType === 'eta' ? 5 : 4}
                      fill={dataPoint.isHidden ? ETA_COLORS.arrivalTimeHidden : ETA_COLORS.arrivalTime}
                      stroke="#fff"
                      strokeWidth={1.5}
                      opacity={getLineOpacity('eta')}
                      className={styles.dataPoint}
                    />
                  )}
                  {/* Regulated point */}
                  {isLineTypeShown('regulated') && dataPoint.hoursUntilRegulated !== null && (
                    <circle
                      cx={x}
                      cy={yScale(dataPoint.hoursUntilRegulated)}
                      r={highlightedLineType === 'regulated' ? 5 : 4}
                      fill={dataPoint.isHidden ? ETA_COLORS.arrivalTimeHidden : ETA_COLORS.regulatedEta}
                      stroke="#fff"
                      strokeWidth={1.5}
                      opacity={getLineOpacity('regulated')}
                      className={styles.dataPoint}
                    />
                  )}
                  {/* Unregulated point */}
                  {isLineTypeShown('unregulated') && dataPoint.hoursUntilUnregulated !== null && (
                    <circle
                      cx={x}
                      cy={yScale(dataPoint.hoursUntilUnregulated)}
                      r={highlightedLineType === 'unregulated' ? 5 : 4}
                      fill={dataPoint.isHidden ? ETA_COLORS.arrivalTimeHidden : ETA_COLORS.unregulatedEta}
                      stroke="#fff"
                      strokeWidth={1.5}
                      opacity={getLineOpacity('unregulated')}
                      className={styles.dataPoint}
                    />
                  )}
                  {/* ATA point */}
                  {isLineTypeShown('ata') && dataPoint.hoursUntilActual !== null && dataPoint.hoursUntilActual >= 0 && (
                    <circle
                      cx={x}
                      cy={yScale(dataPoint.hoursUntilActual)}
                      r={highlightedLineType === 'ata' ? 5 : 4}
                      fill={ETA_COLORS.actualArrival}
                      stroke="#fff"
                      strokeWidth={1.5}
                      opacity={getLineOpacity('ata', 0.7)}
                      className={styles.dataPoint}
                    />
                  )}
                </g>
              );
            })}

          {/* Invisible overlay for hover detection (must be last to capture events) */}
          <rect
            x={0}
            y={0}
            width={innerWidth}
            height={chartInnerHeight}
            fill="transparent"
            className={styles.chartOverlay}
            onMouseMove={handleChartMouseMove}
            onMouseLeave={handleChartMouseLeave}
          />
        </g>
      </svg>

      {/* Legend - interactive: click to toggle visibility, hover to bring to front */}
      <div className={styles.legend}>
        {(
          [
            { type: 'eta' as LineType, color: ETA_COLORS.arrivalTime, label: 'ETA (arrival_time)' },
            { type: 'regulated' as LineType, color: ETA_COLORS.regulatedEta, label: 'Regulated ETA' },
            { type: 'unregulated' as LineType, color: ETA_COLORS.unregulatedEta, label: 'Unregulated ETA' },
            { type: 'ata' as LineType, color: ETA_COLORS.actualArrival, label: 'Actual Arrival (ATA)' },
          ] as const
        ).map(({ type, color, label }) => {
          const isVisible = visibleLineTypes.has(type);
          const isHighlighted = highlightedLineType === type;
          return (
            <div
              key={type}
              className={classnames(styles.legendItem, styles.legendItemInteractive, {
                [styles.legendItemDisabled]: !isVisible,
                [styles.legendItemHighlighted]: isHighlighted,
              })}
              onClick={() => toggleLineType(type)}
              onMouseEnter={() => setHighlightedLineType(type)}
              onMouseLeave={() => setHighlightedLineType(null)}>
              <div className={styles.legendLine} style={{ backgroundColor: color, opacity: isVisible ? 1 : 0.3 }} />
              <span style={{ opacity: isVisible ? 1 : 0.4 }}>{label}</span>
            </div>
          );
        })}
        <div className={styles.legendItem}>
          <div className={styles.legendLine} style={{ backgroundColor: ETA_COLORS.arrivalTimeHidden }} />
          <span>Hidden ETA</span>
        </div>
        <div className={styles.legendItem}>
          <svg width="20" height="10">
            <line
              x1="0"
              y1="8"
              x2="20"
              y2="2"
              stroke={ETA_COLORS.delayThreshold}
              strokeWidth="1"
              strokeDasharray="4,4"
              opacity="0.6"
            />
          </svg>
          <span>Delay Threshold</span>
        </div>
      </div>

      {/* Stop filter checkboxes */}
      <div className={styles.filterSection}>
        <div className={styles.filterHeader}>
          <span className={styles.filterTitle}>Filter Stops:</span>
          <button onClick={selectAll} className={styles.filterButton}>
            Select All
          </button>
          <button onClick={deselectAll} className={styles.filterButton}>
            Deselect All
          </button>
        </div>
        <div className={styles.filterStops}>
          {etaData.map((stop, idx) => {
            const isSelected = selectedStopIds.has(stop.stopId);
            const lineStyleIdx = isSelected ? getLineStyleIndex(stop.stopId) : idx;
            const dashPattern = LINE_DASH_PATTERNS[lineStyleIdx % LINE_DASH_PATTERNS.length];

            return (
              <label key={stop.stopId} className={classnames(styles.stopLabel, isSelected && styles.stopLabelSelected)}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleStop(stop.stopId)}
                  className={styles.stopCheckbox}
                />
                <svg width="24" height="10">
                  <line
                    x1="0"
                    y1="5"
                    x2="24"
                    y2="5"
                    stroke={ETA_COLORS.arrivalTime}
                    strokeWidth="2"
                    strokeDasharray={dashPattern}
                  />
                </svg>
                <span className={styles.stopName}>
                  {stop.sequenceNumber}. {stop.stopName}
                </span>
              </label>
            );
          })}
        </div>
      </div>

      {/* Tooltip */}
      {hover &&
        (() => {
          const tooltipContent = buildHoverTooltipContent();
          if (!tooltipContent) return null;
          return (
            <div
              className={styles.tooltip}
              style={{
                left: hover.clientX + 20,
                top: Math.min(hover.clientY - 10, window.innerHeight - 450),
              }}>
              {tooltipContent}
            </div>
          );
        })()}
    </div>
  );
};

// ETA Accuracy Chart Colors
export const ETA_ACCURACY_COLORS = {
  errorLine: '#9C27B0', // Purple for error line
  zeroLine: '#9E9E9E', // Gray for zero reference line
  positive: '#2196F3', // Blue for pessimistic (arrived early)
  negative: '#FF9800', // Orange for optimistic (arrived late)
};

interface EtaAccuracyChartProps {
  etaData: StopEtaTimeSeries[];
  timeScale: ScaleTime<number, number>;
  width: number;
  margin: { left: number; right: number };
}

// Calculate KPI metrics for a stop's ETA accuracy
interface StopKpiMetrics {
  stopId: number;
  stopName: string;
  sequenceNumber: number;
  mae: number; // Mean Absolute Error in hours
  meanError: number; // Mean Error (bias) in hours - negative = optimistic, positive = pessimistic
  sampleCount: number;
}

const calculateStopKpis = (stop: StopEtaTimeSeries, timeRange?: { start: Date; end: Date }): StopKpiMetrics | null => {
  if (!stop.actualArrivalTime) return null;

  // Calculate error for each data point: ETA - ATA (in hours)
  // Negative = ETA predicted earlier arrival than actual (optimistic)
  // Positive = ETA predicted later arrival than actual (pessimistic)
  const errors: number[] = [];

  stop.dataPoints.forEach((dp) => {
    // Filter by time range if provided
    if (timeRange && (dp.timestamp < timeRange.start || dp.timestamp > timeRange.end)) {
      return;
    }

    if (dp.hoursUntilArrival !== null && dp.hoursUntilActual !== null) {
      // Error = (time until ETA) - (time until ATA)
      // If ETA says 2h and ATA is 3h away, error = -1 (optimistic)
      // If ETA says 4h and ATA is 3h away, error = +1 (pessimistic)
      const errorHours = dp.hoursUntilArrival - dp.hoursUntilActual;
      if (isFinite(errorHours)) {
        errors.push(errorHours);
      }
    }
  });

  if (errors.length === 0) return null;

  const mae = errors.reduce((sum, e) => sum + Math.abs(e), 0) / errors.length;
  const meanError = errors.reduce((sum, e) => sum + e, 0) / errors.length;

  return {
    stopId: stop.stopId,
    stopName: stop.stopName,
    sequenceNumber: stop.sequenceNumber,
    mae,
    meanError,
    sampleCount: errors.length,
  };
};

// Format hours as human-readable string (absolute value only, for MAE)
const formatHoursShort = (hours: number): string => {
  const absHours = Math.abs(hours);
  if (absHours < 1) {
    return `${Math.round(absHours * 60)}m`;
  }
  return `${absHours.toFixed(1)}h`;
};

// Format hours with sign for bias display
const formatBiasShort = (hours: number): string => {
  const absHours = Math.abs(hours);
  // No sign for zero values
  if (absHours === 0 || (absHours < 1 && Math.round(absHours * 60) === 0)) {
    return absHours < 1 ? '0m' : '0.0h';
  }
  const sign = hours > 0 ? '+' : '−';
  if (absHours < 1) {
    return `${sign}${Math.round(absHours * 60)}m`;
  }
  return `${sign}${absHours.toFixed(1)}h`;
};

export const EtaAccuracyChart: React.FC<EtaAccuracyChartProps> = ({ etaData, timeScale, width, margin }) => {
  const { colors } = useTimelineColors();
  const visitedStopsData = useMemo(() => {
    return etaData.filter((stop) => stop.actualArrivalTime !== undefined);
  }, [etaData]);

  // State for selected stops
  const [selectedStopIds, setSelectedStopIds] = useState<Set<number>>(new Set());

  // Update selected stops when visitedStopsData changes
  useEffect(() => {
    if (visitedStopsData.length > 0) {
      setSelectedStopIds(new Set(visitedStopsData.map((s) => s.stopId)));
    }
  }, [visitedStopsData]);

  // Hover state
  const [hover, setHover] = useState<HoverState | null>(null);

  // Toggle stop selection
  const toggleStop = useCallback((stopId: number) => {
    setSelectedStopIds((prev) => {
      const next = new Set(prev);
      if (next.has(stopId)) {
        next.delete(stopId);
      } else {
        next.add(stopId);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedStopIds(new Set(visitedStopsData.map((s) => s.stopId)));
  }, [visitedStopsData]);

  const deselectAll = useCallback(() => {
    setSelectedStopIds(new Set());
  }, []);

  // Get filtered and ordered stops
  const selectedStops = useMemo(() => {
    return visitedStopsData
      .filter((s) => selectedStopIds.has(s.stopId))
      .sort((a, b) => a.sequenceNumber - b.sequenceNumber);
  }, [visitedStopsData, selectedStopIds]);

  // Get the visible time domain from the timeScale
  // Use getTime() for stable primitive dependencies since D3 scale objects can be mutated
  const domainStartMs = timeScale.domain()[0]?.getTime();
  const domainEndMs = timeScale.domain()[1]?.getTime();

  // Calculate KPIs for all visited stops (filtered by visible time range)
  const stopKpis = useMemo(() => {
    const timeRange =
      domainStartMs !== undefined && domainEndMs !== undefined
        ? { start: new Date(domainStartMs), end: new Date(domainEndMs) }
        : undefined;
    const kpis = new Map<number, StopKpiMetrics>();
    visitedStopsData.forEach((stop) => {
      const kpi = calculateStopKpis(stop, timeRange);
      if (kpi) {
        kpis.set(stop.stopId, kpi);
      }
    });
    return kpis;
  }, [visitedStopsData, domainStartMs, domainEndMs]);

  // Line style index
  const getLineStyleIndex = useCallback(
    (stopId: number) => {
      const idx = selectedStops.findIndex((s) => s.stopId === stopId);
      return idx >= 0 ? idx % LINE_DASH_PATTERNS.length : 0;
    },
    [selectedStops],
  );

  // Calculate y-axis domain (error in hours) - symmetric around 0, based on visible data points
  const yDomain = useMemo(() => {
    if (selectedStops.length === 0) return [-2, 2];
    if (domainStartMs === undefined || domainEndMs === undefined) return [-2, 2];

    const visibleStart = new Date(domainStartMs);
    const visibleEnd = new Date(domainEndMs);
    let maxAbsError = 0;

    selectedStops.forEach((stop) => {
      stop.dataPoints.forEach((dp) => {
        // Only consider data points within the visible time range
        if (dp.timestamp < visibleStart || dp.timestamp > visibleEnd) {
          return;
        }

        if (dp.hoursUntilArrival !== null && dp.hoursUntilActual !== null) {
          const error = dp.hoursUntilArrival - dp.hoursUntilActual;
          if (isFinite(error)) {
            maxAbsError = Math.max(maxAbsError, Math.abs(error));
          }
        }
      });
    });

    if (maxAbsError === 0) maxAbsError = 2;

    // Add padding and make symmetric
    const padding = Math.max(0.5, maxAbsError * 0.1);
    const bound = maxAbsError + padding;
    return [-bound, bound];
  }, [selectedStops, domainStartMs, domainEndMs]);

  // Chart dimensions
  const innerWidth = width - margin.left - margin.right;
  const chartHeight = 250;
  const chartMarginTop = 20;
  const chartMarginBottom = 30;
  const chartInnerHeight = chartHeight - chartMarginTop - chartMarginBottom;

  // Y scale (symmetric around 0)
  const yScale = useMemo(
    () => scaleLinear().domain(yDomain).range([chartInnerHeight, 0]).nice(),
    [yDomain, chartInnerHeight],
  );

  // Create line path for accuracy (error over time)
  const createAccuracyLinePath = useCallback(
    (dataPoints: EtaDataPoint[]): string => {
      const points = dataPoints
        .map((dp) => {
          if (dp.hoursUntilArrival !== null && dp.hoursUntilActual !== null) {
            const error = dp.hoursUntilArrival - dp.hoursUntilActual;
            if (isFinite(error)) {
              return {
                x: timeScale(dp.timestamp),
                y: yScale(error),
              };
            }
          }
          return null;
        })
        .filter((p): p is { x: number; y: number } => p !== null);

      if (points.length < 2) return '';
      return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    },
    [timeScale, yScale],
  );

  // Find closest data point
  const findClosestDataPoint = useCallback((stop: StopEtaTimeSeries, targetTime: Date): EtaDataPoint | null => {
    if (stop.dataPoints.length === 0) return null;

    let closest = stop.dataPoints[0];
    let minDiff = Math.abs(closest.timestamp.getTime() - targetTime.getTime());

    for (const dp of stop.dataPoints) {
      const diff = Math.abs(dp.timestamp.getTime() - targetTime.getTime());
      if (diff < minDiff) {
        minDiff = diff;
        closest = dp;
      }
    }

    if (minDiff > 30 * 60 * 1000) return null;
    return closest;
  }, []);

  // Mouse handlers
  const handleChartMouseMove = useCallback(
    (e: React.MouseEvent<SVGRectElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const xPixel = e.clientX - rect.left;
      const hoverTime = timeScale.invert(xPixel);

      setHover({
        visible: true,
        xPixel,
        clientX: e.clientX,
        clientY: e.clientY,
        hoverTime,
      });
    },
    [timeScale],
  );

  const handleChartMouseLeave = useCallback(() => {
    setHover(null);
  }, []);

  // Build tooltip content
  const buildHoverTooltipContent = useCallback(() => {
    if (!hover) return null;

    const stopsWithData = selectedStops
      .map((stop) => {
        const dataPoint = findClosestDataPoint(stop, hover.hoverTime);
        if (!dataPoint || dataPoint.hoursUntilArrival === null || dataPoint.hoursUntilActual === null) return null;
        const error = dataPoint.hoursUntilArrival - dataPoint.hoursUntilActual;
        if (!isFinite(error)) return null;
        return { stop, dataPoint, error };
      })
      .filter((s): s is NonNullable<typeof s> => s !== null);

    if (stopsWithData.length === 0) return null;

    return (
      <div className={styles.tooltipContent}>
        <div className={styles.tooltipHeader}>
          <div className={styles.tooltipTimeLabel}>Time (UTC)</div>
          <div className={styles.tooltipTimeValue}>{formatUtcDateTime(hover.hoverTime)}</div>
        </div>

        {stopsWithData.map(({ stop, error }, index) => {
          const styleIdx = getLineStyleIndex(stop.stopId);
          const dashPattern = LINE_DASH_PATTERNS[styleIdx];
          const isOptimistic = error < 0;

          return (
            <div
              key={stop.stopId}
              className={classnames(styles.tooltipStopSection, index > 0 && styles.tooltipStopSectionWithBorder)}>
              <div className={styles.tooltipStopHeader}>
                <svg width="20" height="10">
                  <line
                    x1="0"
                    y1="5"
                    x2="20"
                    y2="5"
                    stroke={ETA_ACCURACY_COLORS.errorLine}
                    strokeWidth="2"
                    strokeDasharray={dashPattern}
                  />
                </svg>
                <span className={styles.tooltipStopName}>
                  {stop.sequenceNumber}. {stop.stopName}
                </span>
              </div>
              <div className={styles.tooltipErrorSection}>
                <span style={{ color: isOptimistic ? ETA_ACCURACY_COLORS.negative : ETA_ACCURACY_COLORS.positive }}>
                  Error:{' '}
                  <strong>
                    {error > 0 ? '+' : ''}
                    {error.toFixed(2)}h
                  </strong>
                </span>
                <span className={styles.tooltipErrorLabel}>
                  ({isOptimistic ? 'optimistic - arrived later' : 'pessimistic - arrived earlier'})
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [hover, selectedStops, findClosestDataPoint, getLineStyleIndex]);

  if (visitedStopsData.length === 0) {
    return (
      <div className={styles.emptyState} style={{ color: colors.text.secondary }}>
        No visited stops with ETA data available. ETA accuracy is only shown for stops where actual arrival time is
        recorded.
      </div>
    );
  }

  return (
    <div className={styles.chartContainer}>
      {/* Chart */}
      <svg width={width} height={chartHeight} className={styles.chartSvg}>
        <defs>
          <clipPath id="eta-accuracy-chart-clip">
            <rect x={0} y={0} width={innerWidth} height={chartInnerHeight} />
          </clipPath>
        </defs>
        <g transform={`translate(${margin.left},${chartMarginTop})`}>
          {/* Grid lines - horizontal */}
          {yScale.ticks(8).map((tick, i) => (
            <g key={i}>
              <line
                x1={0}
                x2={innerWidth}
                y1={yScale(tick)}
                y2={yScale(tick)}
                stroke={tick === 0 ? ETA_ACCURACY_COLORS.zeroLine : colors.background.grid}
                strokeWidth={tick === 0 ? 2 : 1}
                strokeDasharray={tick === 0 ? '' : '2,2'}
              />
              <text
                x={-8}
                y={yScale(tick)}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize="10px"
                fill={colors.text.secondary}>
                {tick > 0 ? '+' : ''}
                {tick.toFixed(1)}h
              </text>
            </g>
          ))}

          {/* X-axis (time) ticks */}
          {computeUtcTicks(timeScale).map((tick, i) => (
            <g key={i} transform={`translate(${timeScale(tick)},${chartInnerHeight})`}>
              <line y2={6} stroke={colors.background.grid} />
              <text y={18} textAnchor="middle" fontSize="10px" fill={colors.text.secondary}>
                {smartTickFormat(timeScale)(tick)}
              </text>
            </g>
          ))}

          {/* Axes */}
          <line x1={0} x2={innerWidth} y1={chartInnerHeight} y2={chartInnerHeight} stroke={colors.background.grid} />
          <line x1={0} x2={0} y1={0} y2={chartInnerHeight} stroke={colors.background.grid} />

          {/* Y-axis label */}
          <text
            transform={`translate(-45, ${chartInnerHeight / 2}) rotate(-90)`}
            textAnchor="middle"
            fontSize="11px"
            fill={colors.text.primary}>
            ETA Error (hours)
          </text>

          {/* Labels for positive/negative regions */}
          <text
            x={innerWidth + 8}
            y={yScale(yDomain[1] * 0.7)}
            fontSize="10px"
            fill={ETA_ACCURACY_COLORS.positive}
            opacity={0.8}>
            Pessimistic
          </text>
          <text
            x={innerWidth + 8}
            y={yScale(yDomain[0] * 0.7)}
            fontSize="10px"
            fill={ETA_ACCURACY_COLORS.negative}
            opacity={0.8}>
            Optimistic
          </text>

          {/* Draw lines for each selected stop */}
          <g clipPath="url(#eta-accuracy-chart-clip)">
            {selectedStops.map((stop) => {
              const styleIdx = getLineStyleIndex(stop.stopId);
              const dashPattern = LINE_DASH_PATTERNS[styleIdx];
              const path = createAccuracyLinePath(stop.dataPoints);

              if (!path) return null;

              return (
                <path
                  key={stop.stopId}
                  d={path}
                  fill="none"
                  stroke={ETA_ACCURACY_COLORS.errorLine}
                  strokeWidth={2}
                  strokeDasharray={dashPattern}
                  opacity={0.9}
                  className={styles.linePath}
                />
              );
            })}
          </g>

          {/* Hover vertical line */}
          {hover && (
            <line
              x1={hover.xPixel}
              x2={hover.xPixel}
              y1={0}
              y2={chartInnerHeight}
              stroke="#666"
              strokeWidth={1}
              strokeDasharray="4,4"
              className={styles.hoverLine}
            />
          )}

          {/* Data point indicators at hover time */}
          {hover &&
            selectedStops.map((stop) => {
              const dataPoint = findClosestDataPoint(stop, hover.hoverTime);
              if (!dataPoint || dataPoint.hoursUntilArrival === null || dataPoint.hoursUntilActual === null)
                return null;
              const error = dataPoint.hoursUntilArrival - dataPoint.hoursUntilActual;
              if (!isFinite(error)) return null;

              const x = timeScale(dataPoint.timestamp);

              return (
                <circle
                  key={`hover-${stop.stopId}`}
                  cx={x}
                  cy={yScale(error)}
                  r={4}
                  fill={ETA_ACCURACY_COLORS.errorLine}
                  stroke="#fff"
                  strokeWidth={1.5}
                  className={styles.dataPoint}
                />
              );
            })}

          {/* Invisible overlay for hover detection */}
          <rect
            x={0}
            y={0}
            width={innerWidth}
            height={chartInnerHeight}
            fill="transparent"
            className={styles.chartOverlay}
            onMouseMove={handleChartMouseMove}
            onMouseLeave={handleChartMouseLeave}
          />
        </g>
      </svg>

      {/* Legend */}
      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <div className={styles.legendLine} style={{ backgroundColor: ETA_ACCURACY_COLORS.errorLine }} />
          <span>ETA Error (ETA - ATA)</span>
        </div>
        <div className={styles.legendItem}>
          <div className={styles.legendLineZero} style={{ backgroundColor: ETA_ACCURACY_COLORS.zeroLine }} />
          <span>Zero Error (Perfect Prediction)</span>
        </div>
        <div className={styles.legendItem}>
          <span style={{ color: ETA_ACCURACY_COLORS.negative }}>▼ Negative</span>
          <span style={{ color: colors.text.secondary }}>=</span>
          <span style={{ color: ETA_ACCURACY_COLORS.negative }}>Optimistic</span>
          <span>(arrived later than predicted)</span>
        </div>
        <div className={styles.legendItem}>
          <span style={{ color: ETA_ACCURACY_COLORS.positive }}>▲ Positive</span>
          <span style={{ color: colors.text.secondary }}>=</span>
          <span style={{ color: ETA_ACCURACY_COLORS.positive }}>Pessimistic</span>
          <span>(arrived earlier than predicted)</span>
        </div>
      </div>

      {/* Stop filter with KPIs */}
      <div className={styles.filterSection}>
        <div className={styles.filterHeader}>
          <span className={styles.filterTitle}>Filter Stops:</span>
          <button onClick={selectAll} className={styles.filterButton}>
            Select All
          </button>
          <button onClick={deselectAll} className={styles.filterButton}>
            Deselect All
          </button>
        </div>
        <div className={styles.filterStops}>
          {visitedStopsData.map((stop, idx) => {
            const isSelected = selectedStopIds.has(stop.stopId);
            const lineStyleIdx = isSelected ? getLineStyleIndex(stop.stopId) : idx;
            const dashPattern = LINE_DASH_PATTERNS[lineStyleIdx % LINE_DASH_PATTERNS.length];
            const kpi = stopKpis.get(stop.stopId);

            return (
              <label
                key={stop.stopId}
                className={classnames(
                  styles.stopLabel,
                  styles.stopLabelAccuracy,
                  isSelected && styles.stopLabelAccuracySelected,
                )}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleStop(stop.stopId)}
                  className={styles.stopCheckbox}
                />
                <svg width="24" height="10">
                  <line
                    x1="0"
                    y1="5"
                    x2="24"
                    y2="5"
                    stroke={ETA_ACCURACY_COLORS.errorLine}
                    strokeWidth="2"
                    strokeDasharray={dashPattern}
                  />
                </svg>
                <span className={styles.stopName}>
                  {stop.sequenceNumber}. {stop.stopName}
                </span>
                {kpi && (
                  <span
                    className={styles.kpiBadge}
                    title={`Mean Absolute Error: ${kpi.mae.toFixed(2)}h | Mean Error (Bias): ${kpi.meanError > 0 ? '+' : ''}${kpi.meanError.toFixed(2)}h | Samples: ${kpi.sampleCount} | KPIs are calculated for the current zoom range`}>
                    MAE: {formatHoursShort(kpi.mae)}
                    <span
                      style={{
                        marginLeft: '6px',
                        color: kpi.meanError < 0 ? ETA_ACCURACY_COLORS.negative : ETA_ACCURACY_COLORS.positive,
                      }}>
                      Bias: {formatBiasShort(kpi.meanError)}
                    </span>
                  </span>
                )}
              </label>
            );
          })}
        </div>
      </div>

      {/* Tooltip */}
      {hover &&
        (() => {
          const tooltipContent = buildHoverTooltipContent();
          if (!tooltipContent) return null;
          return (
            <div
              className={styles.tooltip}
              style={{
                left: hover.clientX + 20,
                top: Math.min(hover.clientY - 10, window.innerHeight - 350),
              }}>
              {tooltipContent}
            </div>
          );
        })()}
    </div>
  );
};

// Helper function to get a value from an object using multiple possible keys
const getField = <T,>(obj: Record<string, unknown>, ...keys: string[]): T | undefined => {
  for (const key of keys) {
    if (obj[key] !== undefined) {
      return obj[key] as T;
    }
  }
  return undefined;
};

// Helper function to transform raw BigQuery ETA messages into StopEtaTimeSeries
export const transformEtaMessagesToTimeSeries = (
  messages: Array<{ produced_at: string; message: string }>,
  stops: Stop[],
): StopEtaTimeSeries[] => {
  // Build a map of stop_id to stop info (support both number and string keys)
  const stopInfoMap = new Map<
    number,
    {
      stopId: number;
      stopName: string;
      sequenceNumber: number;
      actualArrivalTime: Date | undefined;
    }
  >();

  stops.forEach((stop) => {
    stopInfoMap.set(stop.stop_id, {
      stopId: stop.stop_id,
      stopName: stop.location.name,
      sequenceNumber: stop.display_sequence_number,
      actualArrivalTime: stop.arrival_time ? new Date(stop.arrival_time) : undefined,
    });
  });

  // Collect data points for each stop
  const stopDataMap = new Map<number, EtaDataPoint[]>();

  messages.forEach((msg) => {
    try {
      const producedAt = new Date(msg.produced_at);
      const payload = JSON.parse(msg.message);

      // Look for stops in the ETA message (try various field names)
      const etaStops = payload.stops || payload.Stops || payload.stop_etas || payload.stopEtas || [];

      // Get the etas array for fallback when arrival_time is missing (hidden/unstable ETA)
      const etasArray: Array<{ eta_type?: string; stops?: Array<Record<string, unknown>> }> = payload.etas || [];

      // Find the ML_MODEL eta for fallback - used when main stops[].arrival_time is empty
      const mlModelEta = etasArray.find((eta) => eta.eta_type === 'ML_MODEL');
      const mlModelStops = mlModelEta?.stops || [];

      // Find the INTERNAL eta - it has the proper regulated ETA even for ML cases
      // (top-level stops[].regulated_arrival_time is overwritten with ML arrival time for ML ETAs)
      const internalEta = etasArray.find((eta) => eta.eta_type === 'INTERNAL');
      const internalEtaStops = internalEta?.stops || [];

      // Helper to find fallback arrival time from ML_MODEL eta
      const getFallbackArrivalTime = (stopId: number): string | undefined => {
        const mlStop = mlModelStops.find((s) => String(getField(s, 'stop_id')) === String(stopId));
        if (isNil(mlStop)) return undefined;
        return getField<string>(mlStop, 'arrival_time');
      };

      // Helper to find the proper regulated arrival time from INTERNAL eta
      // The INTERNAL eta's arrival_time IS the regulated ETA (by definition - INTERNAL uses regulated calculations)
      const getInternalRegulatedArrivalTime = (stopId: number): string | undefined => {
        const internalStop = internalEtaStops.find((s) => String(getField(s, 'stop_id')) === String(stopId));
        if (isNil(internalStop)) return undefined;
        // Prefer arrival_time (always the regulated value), fall back to regulated_arrival_time
        return (
          getField<string>(internalStop, 'arrival_time') || getField<string>(internalStop, 'regulated_arrival_time')
        );
      };

      etaStops.forEach((etaStop: Record<string, unknown>) => {
        const etaStopIdRaw = getField<string>(etaStop, 'stop_id');
        if (isNil(etaStopIdRaw)) return;

        const etaStopId = parseInt(etaStopIdRaw, 10);
        const stopInfo = stopInfoMap.get(etaStopId);
        if (isNil(stopInfo)) return;

        // Parse ETA arrival time
        let arrivalTimeStr = getField<string>(etaStop, 'arrival_time');

        // Check if we need to use fallback from ML_MODEL eta (hidden/unstable ETA case)
        // When ETA is unstable, stops[].arrival_time will be empty but etas[].stops has the value
        let isHiddenEta = false;
        if (!arrivalTimeStr) {
          const fallbackArrival = getFallbackArrivalTime(etaStopId);
          if (fallbackArrival) {
            arrivalTimeStr = fallbackArrival;
            isHiddenEta = true; // Mark as hidden since we're using fallback from ML_MODEL
          }
        }

        // For regulated ETA, prefer the value from INTERNAL eta (etas[eta_type=INTERNAL].stops[])
        // because for ML ETA cases the top-level stops[].regulated_arrival_time is overwritten with ML arrival time
        const regulatedStr =
          getInternalRegulatedArrivalTime(etaStopId) || getField<string>(etaStop, 'regulated_arrival_time');
        const unregulatedStr = getField<string>(etaStop, 'unregulated_arrival_time');

        // Extract additional tooltip fields
        const status = getField<string>(etaStop, 'arrival_time_status') || null;
        const accuracy = getField<string>(etaStop, 'arrival_time_accuracy') || null;

        // Handle leg_origin which might be an object with id
        const legOrigin = getField<{ id?: string } | string>(etaStop, 'leg_origin');
        const legOriginId = legOrigin ? (typeof legOrigin === 'object' ? legOrigin.id : legOrigin) || null : null;

        const legDistanceMeters = getField<number>(etaStop, 'leg_distance_in_meters') || null;
        const legBreakTimeSeconds = getField<number>(etaStop, 'leg_total_break_time_in_seconds') || null;

        // Calculate hours until each ETA
        const calcHoursUntil = (etaStr: string | undefined): number | null => {
          if (!etaStr) return null;
          const etaTime = new Date(etaStr);
          if (isNaN(etaTime.getTime())) return null;
          const diffMs = etaTime.getTime() - producedAt.getTime();
          return diffMs / (1000 * 60 * 60); // Convert to hours
        };

        const hoursUntilArrival = calcHoursUntil(arrivalTimeStr);
        if (hoursUntilArrival === null) return; // Skip if no arrival time (even from fallback)

        // Calculate hours until actual arrival (for ATA line)
        const hoursUntilActual = stopInfo.actualArrivalTime
          ? (stopInfo.actualArrivalTime.getTime() - producedAt.getTime()) / (1000 * 60 * 60)
          : null;

        const dataPoint: EtaDataPoint = {
          timestamp: producedAt,
          stopId: etaStopId,
          hoursUntilArrival,
          hoursUntilRegulated: calcHoursUntil(regulatedStr),
          hoursUntilUnregulated: calcHoursUntil(unregulatedStr),
          hoursUntilActual,
          status,
          accuracy,
          legOriginId,
          legDistanceMeters,
          legBreakTimeSeconds,
          isHidden: isHiddenEta,
        };

        if (!stopDataMap.has(etaStopId)) {
          stopDataMap.set(etaStopId, []);
        }
        const stopDataPoints = stopDataMap.get(etaStopId);
        if (stopDataPoints) {
          stopDataPoints.push(dataPoint);
        }
      });
    } catch (e) {
      console.warn('[ETA Chart] Failed to parse ETA message:', e, msg);
    }
  });

  // Build the result array
  const result: StopEtaTimeSeries[] = [];

  stopDataMap.forEach((dataPoints, stopId) => {
    const stopInfo = stopInfoMap.get(stopId);
    if (!stopInfo) return;

    // Sort data points by timestamp
    dataPoints.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

    result.push({
      stopId,
      stopName: stopInfo.stopName,
      sequenceNumber: stopInfo.sequenceNumber,
      actualArrivalTime: stopInfo.actualArrivalTime,
      dataPoints,
    });
  });

  // Sort by sequence number
  result.sort((a, b) => a.sequenceNumber - b.sequenceNumber);

  return result;
};
